'use client';

// Page-level instrumentation for comparison pages (A-1/A-2). Mounted once per
// page; records the view, scroll depth, footer-CTA clicks (delegated on
// [data-compare-cta]), and AI side-column impression/exit
// ([data-compare-sidecolumn]). Conversions are attributed via the view event's
// page/type/vertical props.

import { useEffect } from 'react';

import { track } from '@/lib/compare/track';

export function CompareAnalytics({
  slug,
  type,
  vertical,
}: {
  slug: string;
  type: string;
  vertical: string;
}) {
  useEffect(() => {
    const base = { slug, type, vertical };
    track('compare_view', base);

    // Scroll depth milestones.
    const hit = new Set<number>();
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      if (max <= 0) return;
      const pct = Math.round((doc.scrollTop / max) * 100);
      for (const m of [25, 50, 75, 100]) {
        if (pct >= m && !hit.has(m)) {
          hit.add(m);
          track('compare_scroll_depth', { ...base, depth: m });
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Footer / pitch CTA clicks.
    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement)?.closest('[data-compare-cta]');
      if (el) {
        track('compare_cta_click', {
          ...base,
          cta: el.getAttribute('data-compare-cta') ?? 'unknown',
        });
      }
    };
    document.addEventListener('click', onClick);

    // AI side-column impression / exit (Pitch #2).
    let observer: IntersectionObserver | null = null;
    const sidecol = document.querySelector('[data-compare-sidecolumn]');
    if (sidecol && 'IntersectionObserver' in window) {
      let seen = false;
      let exited = false;
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && !seen) {
              seen = true;
              track('compare_sidecolumn_view', base);
            } else if (!entry.isIntersecting && seen && !exited) {
              // Record the leave once; otherwise every off-screen callback
              // re-fires and inflates the exit count.
              exited = true;
              track('compare_sidecolumn_exit', base);
              observer?.disconnect();
            }
          }
        },
        { threshold: 0.3 },
      );
      observer.observe(sidecol);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('click', onClick);
      observer?.disconnect();
    };
  }, [slug, type, vertical]);

  return null;
}
